import { call, callWithFallback, connection, request, uploadFile } from "./client";
import * as sample from "./sample";
import { setAppCurrency } from "../utils/format";

export { connection };

export const api = {
	dashboard: () => callWithFallback("tripplan.api.dashboard.get_dashboard", {}, sample.dashboard),

	listEnquiries: (args = {}) => call("tripplan.api.enquiry.list_enquiries", args),
	getEnquiry: (name) => call("tripplan.api.enquiry.get_enquiry", { name }),
	saveEnquiry: (payload) => call("tripplan.api.enquiry.save_enquiry", { payload }),
	prepareEnquiry: (name) => call("tripplan.api.enquiry.prepare_quotation", { name }),
	markEnquiryQuoted: (name, booking) => call("tripplan.api.enquiry.mark_quoted", { name, booking }),
	markEnquiryLost: (name) => call("tripplan.api.enquiry.mark_lost", { name }),
	submitPublicEnquiry: async (args) => {
		const payload = await request("tripplan.api.enquiry.submit_public", args, { skipAuthRedirect: true });
		return payload.message;
	},

	listPackages: (args = {}) =>
		callWithFallback("tripplan.api.package.list_packages", args, () =>
			filterList(sample.packages, args, ["package_name", "destination", "name"]),
		),

	getPackage: (name) =>
		callWithFallback("tripplan.api.package.get_package", { name }, () => ({
			...sample.packageDetail,
			name,
		})),

	savePackage: (payload) => call("tripplan.api.package.save_package", { payload }),

	duplicatePackage: (name) => call("tripplan.api.package.duplicate_package", { name }),
	listFiles: async (doctype, name) => {
		try {
			const files = await call("tripplan.api.package.list_files", { doctype, name });
			if (Array.isArray(files) && files.length && files[0].attached_to_field !== undefined) {
				return files;
			}
			if (Array.isArray(files) && files.length) {
				// Older backends omit attached_to_field — enrich from File list.
				try {
					const rows = await call("frappe.client.get_list", {
						doctype: "File",
						filters: { attached_to_doctype: doctype, attached_to_name: name },
						fields: ["name", "file_name", "file_url", "file_size", "is_private", "attached_to_field"],
						order_by: "creation desc",
						limit_page_length: 100,
					});
					if (Array.isArray(rows) && rows.length) return rows;
				} catch {
					/* keep tripplan list */
				}
				return files;
			}
			return files || [];
		} catch {
			return call("frappe.client.get_list", {
				doctype: "File",
				filters: { attached_to_doctype: doctype, attached_to_name: name },
				fields: ["name", "file_name", "file_url", "file_size", "is_private", "attached_to_field"],
				order_by: "creation desc",
				limit_page_length: 100,
			});
		}
	},
	uploadFile: (doctype, name, file, options = {}) => uploadFile(doctype, name, file, options),
	deleteFile: (name) => call("frappe.client.delete", { doctype: "File", name }),
	ensureDayFileName: async (fileName, dayNo, preferredName = "") => {
		const day = Number(dayNo) || 1;
		try {
			return await call("tripplan.api.package.ensure_day_file_name", {
				file_name: fileName,
				day_no: day,
			});
		} catch {
			const value = preferredName || `day-${day}-photo.jpg`;
			const field = `day_${day}_photo`;
			await call("frappe.client.set_value", {
				doctype: "File",
				name: fileName,
				fieldname: { file_name: value, attached_to_field: field },
			});
			return { name: fileName, file_name: value, attached_to_field: field, day_no: day };
		}
	},
	markVoucherPrinted: (booking, service) => call("tripplan.api.booking.mark_voucher_printed", { booking, service }),

	listBookings: (args = {}) =>
		callWithFallback("tripplan.api.booking.list_bookings", args, () =>
			filterList(sample.bookings, args, ["booking_name", "customer_name", "name", "booking_no"]),
		),

	getBooking: (name) =>
		callWithFallback("tripplan.api.booking.get_booking", { name }, () => ({
			...sample.bookingDetail,
			name,
		})),

	previewQuote: (args) => call("tripplan.api.booking.preview_quote", args),
	createBooking: (args) => call("tripplan.api.booking.create_booking", args),
	addService: (booking, service) => call("tripplan.api.booking.add_service", { booking, service }),
	updateService: (booking, rowName, values) =>
		call("tripplan.api.booking.update_service", { booking, row_name: rowName, values }),
	removeService: (booking, rowName) =>
		call("tripplan.api.booking.remove_service", { booking, row_name: rowName }),
	reprice: (booking) => call("tripplan.api.booking.reprice", { booking }),
	convertQuote: (booking) => call("tripplan.api.booking.convert_quote", { booking }),
	cancelQuote: (booking, reason) => call("tripplan.api.booking.cancel_quote", { booking, reason }),
	setRooms: (booking, rooms) => call("tripplan.api.booking.set_rooms", { booking, rooms }),
	setPassengers: (booking, passengers) => call("tripplan.api.booking.set_passengers", { booking, passengers }),
	listSupplierRequests: (serviceStatus = "RQ") =>
		call("tripplan.api.booking.list_supplier_requests", { service_status: serviceStatus }),
	setServiceStatus: (booking, rowName, serviceStatus) =>
		call("tripplan.api.booking.set_service_status", {
			booking,
			row_name: rowName,
			service_status: serviceStatus,
		}),

	listCustomers: (args = {}) =>
		callWithFallback("tripplan.api.masters.list_customers", args, () =>
			filterList(sample.customers, args, ["customer_name", "name"]),
		),

	listSuppliers: (args = {}) =>
		callWithFallback("tripplan.api.masters.list_suppliers", args, () =>
			filterList(sample.suppliers, args, ["supplier_name", "name"]),
		),

	listProducts: (args = {}) =>
		callWithFallback("tripplan.api.masters.list_products", args, () =>
			filterList(sample.products, args, ["product_name", "product_code", "supplier"]),
		),

	listRates: (args = {}) =>
		callWithFallback("tripplan.api.masters.list_rates", args, () =>
			filterList(sample.rates, args, ["product", "option_code"]),
		),

	listMasters: () => callWithFallback("tripplan.api.masters.list_masters", {}, sample.masters),
	googlePlacesSettings: async () => (await request("tripplan.api.places.get_google_settings")).message,
	saveGooglePlacesSettings: async (args) => (await request("tripplan.api.places.save_google_settings", args)).message,
	paymentSettings: async () => (await request("tripplan.api.accounts.get_payment_settings")).message,
	savePaymentSettings: async (args) => (await request("tripplan.api.accounts.save_payment_settings", args)).message,
	searchDoctype: async (doctype, term, fields = ["name"]) => {
		const filters = term ? { name: ["like", `%${term}%`] } : {};
		return (
			(await call("frappe.client.get_list", {
				doctype,
				filters,
				fields,
				limit_page_length: 20,
				order_by: "name asc",
			})) || []
		);
	},
	searchAccounts: async (term) => {
		const orFilters = term
			? {
					name: ["like", `%${term}%`],
					account_name: ["like", `%${term}%`],
				}
			: undefined;
		return (
			(await call("frappe.client.get_list", {
				doctype: "Account",
				filters: { is_group: 0, disabled: 0, account_type: ["in", ["Cash", "Bank"]] },
				or_filters: orFilters,
				fields: ["name", "account_type", "account_currency"],
				limit_page_length: 20,
				order_by: "name asc",
			})) || []
		);
	},
	googleAutocomplete: async (query) => (await request("tripplan.api.places.autocomplete", { query })).message,
	googlePlaceDetails: async (placeId) => (await request("tripplan.api.places.place_details", { place_id: placeId })).message,
	googlePlacesAlong: async (origin, destination) =>
		(await request("tripplan.api.places.places_along", { origin, destination })).message,

	searchRecords: (args = {}) => call("tripplan.api.masters.search_records", args),
	companySettings: async () => {
		const company = await companyDefaults();
		const currency = company.default_currency || "USD";
		setAppCurrency(currency);
		return { company: company.name || "", default_currency: currency };
	},
	exchangeRate: (fromCurrency, toCurrency) =>
		call("tripplan.api.masters.get_exchange_rate", {
			from_currency: fromCurrency,
			to_currency: toCurrency,
		}),

	createCustomer: async (args) => {
		const customerType = args.customer_type || "Individual";
		return insertDoc("Customer", {
			customer_name: args.customer_name,
			customer_type: customerType,
			customer_group:
				args.customer_group ||
				(await leafName("Customer Group", customerType === "Company" ? "Commercial" : "Individual")),
			territory: args.territory || (await leafName("Territory")),
			email_id: args.email_id,
			mobile_no: args.mobile_no,
		});
	},
	createSupplier: async (args) =>
		insertDoc("Supplier", {
			supplier_name: args.supplier_name,
			supplier_group: args.supplier_group || (await leafName("Supplier Group")),
			country: args.country,
			email_id: args.email_id,
		}),
	createProduct: async (args) => {
		const company = await companyDefaults();
		return insertDoc("TP Product", {
			product_name: args.product_name,
			product_code: args.product_code,
			supplier: args.supplier,
			service_type: args.service_type,
			location: args.location,
			currency: args.currency || company.default_currency || "USD",
			is_active: 1,
		});
	},
	createRate: async (args) => {
		const company = await companyDefaults();
		return insertDoc("TP Rate", {
			product: args.product,
			valid_from: args.valid_from,
			valid_to: args.valid_to,
			currency: args.currency || company.default_currency || "USD",
			basis: args.basis || "Per Person",
			cost_adult: args.cost_adult,
			sell_adult: args.sell_adult,
			cost_child: args.cost_child,
			sell_child: args.sell_child,
			cost_infant: args.cost_infant,
			sell_infant: args.sell_infant,
			option_code: args.option_code,
			price_code: args.price_code,
			is_active: 1,
		});
	},
	createLocation: (args) =>
		insertDoc("TP Location", {
			code: args.code,
			location_name: args.location_name,
			country: args.country,
			region: args.region,
			is_active: 1,
		}),
	createServiceType: (args) =>
		insertDoc("TP Service Type", {
			code: args.code,
			service_type_name: args.service_type_name,
			category: args.category || "Other",
		}),
	createPriceCode: (args) =>
		insertDoc("TP Price Code", {
			code: args.code,
			description: args.description,
		}),

	listActivities: async () => {
		await ensureActivityDoctype();
		const rows = await call("frappe.client.get_list", {
			doctype: "TP Activity",
			fields: [
				"name",
				"activity_name",
				"source",
				"supplier",
				"currency",
				"price",
				"mock_type",
				"mock_price",
				"final_amount",
			],
			order_by: "activity_name asc",
			limit_page_length: 200,
		});
		const options = await listActivityOptions();
		const optionsByParent = new Map();
		for (const option of options) {
			const list = optionsByParent.get(option.parent) || [];
			list.push(option);
			optionsByParent.set(option.parent, list);
		}
		return (Array.isArray(rows) ? rows : []).map((row) => mapActivity(row, optionsByParent.get(row.name) || []));
	},

	getActivity: async (name) => {
		await ensureActivityDoctype();
		const doc = await call("frappe.client.get", { doctype: "TP Activity", name });
		return mapActivity(doc, doc.options || []);
	},

	createActivity: async (args) => {
		const doc = activityPayload(args);
		const company = await companyDefaults();
		doc.currency = company.default_currency || "USD";
		await ensureActivityDoctype();
		try {
			return await insertDoc("TP Activity", doc);
		} catch (error) {
			if (!/No module named/i.test(error.message || "")) throw error;
			await placeActivityDoctypeInTripPlan();
			return insertDoc("TP Activity", doc);
		}
	},

	updateActivity: async (name, args) => {
		const doc = activityPayload(args);
		await ensureActivityDoctype();
		const existing = await call("frappe.client.get", { doctype: "TP Activity", name });
		await call("frappe.desk.form.save.savedocs", {
			doc: {
				...existing,
				...doc,
				name,
				doctype: "TP Activity",
				supplier: doc.supplier || "",
			},
			action: "Save",
		});
		return name;
	},

	deleteActivity: (name) => call("frappe.client.delete", { doctype: "TP Activity", name }),

	listInvoices: (args = {}) =>
		callWithFallback("tripplan.api.accounts.list_invoices", args, () =>
			filterList(sample.invoices, args, ["name", "customer_name"]),
		),

	listPayments: (args = {}) =>
		callWithFallback("tripplan.api.accounts.list_payments", args, () =>
			filterList(sample.payments, args, ["name", "party_name"]),
		),

	createSalesInvoice: (booking) => call("tripplan.api.accounts.create_sales_invoice", { booking }),
	createPurchaseInvoices: (booking) => call("tripplan.api.accounts.create_purchase_invoices", { booking }),
	submitSalesInvoice: (name) => call("tripplan.api.accounts.submit_sales_invoice", { name }),
	submitPurchaseInvoice: (name) => call("tripplan.api.accounts.submit_purchase_invoice", { name }),
	managementReport: (args = {}) => call("tripplan.api.reports.management_report", args),
	createPayment: (args) =>
		call("tripplan.api.accounts.create_payment", {
			party: args.party,
			paid_amount: args.paid_amount,
			currency: args.currency,
			mode_of_payment: args.mode_of_payment,
			reference_no: args.reference_no,
			posting_date: args.posting_date,
			booking: args.booking,
		}),
};

function activityTotal(price, mode, mockPrice) {
	const base = Number(price || 0);
	const mock = Number(mockPrice || 0);
	const extra = mode === "percent" ? (base * mock) / 100 : mock;
	return base + extra;
}

function compact(fields) {
	return Object.fromEntries(
		Object.entries(fields).filter(([, value]) => value !== undefined && value !== null && value !== ""),
	);
}

function mapOption(row) {
	const mode = row.mock_type === "Percentage" || row.mock_mode === "percent" ? "percent" : "amount";
	const price = Number(row.price || 0);
	const mockPrice = Number(row.mock_price || 0);
	return {
		option_name: row.option_name || "",
		price,
		mock_mode: mode,
		mock_price: mockPrice,
		final_amount: Number(row.final_amount ?? activityTotal(price, mode, mockPrice)),
	};
}

function mapActivity(row, options = []) {
	const mode = row.mock_type === "Percentage" || row.mock_mode === "percent" ? "percent" : "amount";
	const source = row.source === "Outsource" || row.source === "outsource" ? "outsource" : "own";
	return {
		name: row.name,
		activity_name: row.activity_name,
		source,
		supplier: source === "outsource" ? row.supplier || "" : "",
		price: Number(row.price || 0),
		mock_mode: mode,
		mock_price: Number(row.mock_price || 0),
		final_amount: Number(row.final_amount || 0),
		currency: row.currency,
		options: (options || []).map(mapOption),
	};
}

function activityPayload(args) {
	const source = args.source === "outsource" || args.source === "Outsource" ? "Outsource" : "Own";
	const activityName = String(args.activity_name || "").trim();
	const price = Number(args.price || 0);
	const mockMode = args.mock_mode === "percent" ? "percent" : "amount";
	const mockPrice = Number(args.mock_price || 0);
	if (!activityName) throw new Error("Activity name is required");
	if (source === "Outsource" && !args.supplier) throw new Error("Supplier is required for an outsourced activity");
	if (price < 0 || mockPrice < 0) throw new Error("Price and mock price cannot be negative");

	const options = (args.options || [])
		.filter((option) => String(option.option_name || "").trim())
		.map((option) => {
			const optionMode = option.mock_mode === "percent" ? "percent" : "amount";
			const optionPrice = Number(option.price || 0);
			const optionMock = Number(option.mock_price || 0);
			if (optionPrice < 0 || optionMock < 0) throw new Error("Option price and mock price cannot be negative");
			return {
				doctype: "TP Activity Option",
				option_name: String(option.option_name).trim(),
				price: optionPrice,
				mock_type: optionMode === "percent" ? "Percentage" : "Amount",
				mock_price: optionMock,
				final_amount: activityTotal(optionPrice, optionMode, optionMock),
			};
		});

	const payload = {
		activity_name: activityName,
		source,
		supplier: source === "Outsource" ? args.supplier : "",
		price,
		mock_type: mockMode === "percent" ? "Percentage" : "Amount",
		mock_price: mockPrice,
		final_amount: activityTotal(price, mockMode, mockPrice),
	};
	if (Array.isArray(args.options)) payload.options = options;
	return payload;
}

async function listActivityOptions() {
	try {
		const rows = await call("frappe.client.get_list", {
			doctype: "TP Activity Option",
			fields: ["parent", "option_name", "price", "mock_type", "mock_price", "final_amount"],
			limit_page_length: 500,
		});
		return (Array.isArray(rows) ? rows : []).map((row) => ({ ...mapOption(row), parent: row.parent }));
	} catch {
		return [];
	}
}

async function ensureActivityOptionDoctype() {
	try {
		await call("frappe.client.get", { doctype: "DocType", name: "TP Activity Option" });
		return;
	} catch (error) {
		if (!/not found|does not exist|DoesNotExist/i.test(error.message || "")) throw error;
	}
	const field = (values) => ({ doctype: "DocField", ...values });
	await insertDoc("DocType", {
		name: "TP Activity Option",
		module: "Tour Operations",
		custom: 1,
		istable: 1,
		editable_grid: 1,
		engine: "InnoDB",
		fields: [
			field({ fieldname: "option_name", fieldtype: "Data", label: "Option Name", reqd: 1, in_list_view: 1 }),
			field({ fieldname: "price", fieldtype: "Currency", label: "Price", reqd: 1, in_list_view: 1 }),
			field({ fieldname: "mock_type", fieldtype: "Select", label: "Mock Type", options: "Amount\nPercentage", default: "Amount", reqd: 1, in_list_view: 1 }),
			field({ fieldname: "mock_price", fieldtype: "Float", label: "Mock Price", precision: "2", default: "0", in_list_view: 1 }),
			field({ fieldname: "final_amount", fieldtype: "Currency", label: "Final Amount", read_only: 1, in_list_view: 1 }),
		],
	});
}

let activityDoctypeReady = false;

/**
 * A DocType that is not in the TripPlan app is resolved under Core, which
 * throws "No module named frappe.core.doctype.tp_activity".
 * Keep TP Activity on the Tour Operations module so it belongs to tripplan.
 */
async function ensureActivityDoctype() {
	if (activityDoctypeReady) return;
	await ensureActivityOptionDoctype();
	let current = null;
	try {
		current = await call("frappe.client.get", { doctype: "DocType", name: "TP Activity" });
	} catch (error) {
		if (!/not found|does not exist|DoesNotExist/i.test(error.message || "")) throw error;
	}
	if (!current?.name) {
		await insertDoc("DocType", activityDoctypeDoc());
		activityDoctypeReady = true;
		return;
	}
	const hasOptions = (current.fields || []).some((field) => field.fieldname === "options");
	if (current.module === "Tour Operations" && hasOptions) {
		activityDoctypeReady = true;
		return;
	}
	await placeActivityDoctypeInTripPlan(current);
	activityDoctypeReady = true;
}

async function placeActivityDoctypeInTripPlan(current) {
	const existing = current || (await call("frappe.client.get", { doctype: "DocType", name: "TP Activity" }));
	const definition = activityDoctypeDoc();
	const present = new Set((existing.fields || []).map((field) => field.fieldname));
	const fields = [...(existing.fields || [])];
	for (const field of definition.fields) {
		if (!present.has(field.fieldname)) fields.push(field);
	}
	await call("frappe.desk.form.save.savedocs", {
		doc: {
			...existing,
			module: "Tour Operations",
			custom: 1,
			autoname: existing.autoname || definition.autoname,
			naming_rule: existing.naming_rule || definition.naming_rule,
			fields,
			permissions: existing.permissions?.length ? existing.permissions : definition.permissions,
		},
		action: "Save",
	});
}

function activityDoctypeDoc() {
	const field = (values) => ({ doctype: "DocField", ...values });
	const perm = (role, canDelete) => ({
		doctype: "DocPerm",
		role,
		read: 1,
		write: 1,
		create: 1,
		delete: canDelete ? 1 : 0,
		report: 1,
		export: 1,
		print: 1,
		email: 1,
		share: 1,
	});
	return {
		name: "TP Activity",
		module: "Tour Operations",
		custom: 1,
		autoname: "naming_series:",
		naming_rule: 'By "Naming Series" field',
		engine: "InnoDB",
		title_field: "activity_name",
		search_fields: "activity_name,source",
		track_changes: 1,
		fields: [
			field({ fieldname: "naming_series", fieldtype: "Select", label: "Series", options: "ACT-.#####", default: "ACT-.#####", hidden: 1 }),
			field({ fieldname: "activity_name", fieldtype: "Data", label: "Activity Name", reqd: 1, in_list_view: 1 }),
			field({ fieldname: "source", fieldtype: "Select", label: "Source", options: "Own\nOutsource", default: "Own", reqd: 1, in_list_view: 1 }),
			field({
				fieldname: "supplier",
				fieldtype: "Link",
				label: "Supplier",
				options: "Supplier",
				depends_on: 'eval:doc.source=="Outsource"',
				mandatory_depends_on: 'eval:doc.source=="Outsource"',
				in_list_view: 1,
			}),
			field({ fieldname: "column_break_main", fieldtype: "Column Break" }),
			field({ fieldname: "currency", fieldtype: "Link", label: "Currency", options: "Currency", reqd: 1 }),
			field({ fieldname: "price", fieldtype: "Currency", label: "Price", options: "currency", reqd: 1, in_list_view: 1 }),
			field({ fieldname: "mock_type", fieldtype: "Select", label: "Mock Type", options: "Amount\nPercentage", default: "Amount", reqd: 1 }),
			field({ fieldname: "mock_price", fieldtype: "Float", label: "Mock Price", precision: "2", default: "0" }),
			field({ fieldname: "final_amount", fieldtype: "Currency", label: "Final Amount", options: "currency", read_only: 1, in_list_view: 1 }),
			field({ fieldname: "section_break_options", fieldtype: "Section Break", label: "Options" }),
			field({ fieldname: "options", fieldtype: "Table", label: "Options", options: "TP Activity Option" }),
		],
		permissions: [perm("System Manager", true), perm("Sales User", false)],
	};
}

/** Insert through Frappe's standard API so create works on the live site. */
async function insertDoc(doctype, fields) {
	const doc = await call("frappe.client.insert", { doc: { doctype, ...compact(fields) } });
	return doc?.name || doc;
}

const leafCache = new Map();

/** ERPNext rejects tree nodes such as "All Customer Groups". Pick a leaf. */
async function leafName(doctype, preferred) {
	const key = `${doctype}:${preferred || ""}`;
	if (leafCache.has(key)) return leafCache.get(key);

	let name = "";
	if (preferred) {
		const match = await call("frappe.client.get_list", {
			doctype,
			filters: { name: preferred, is_group: 0 },
			fields: ["name"],
			limit_page_length: 1,
		});
		name = Array.isArray(match) ? match[0]?.name || "" : "";
	}
	if (!name) {
		const rows = await call("frappe.client.get_list", {
			doctype,
			filters: { is_group: 0 },
			fields: ["name"],
			order_by: "lft asc",
			limit_page_length: 1,
		});
		name = Array.isArray(rows) ? rows[0]?.name || "" : "";
	}
	if (!name) throw new Error(`Add a usable ${doctype} before creating this record`);
	leafCache.set(key, name);
	return name;
}

let companyCache;

async function companyDefaults() {
	if (companyCache) return companyCache;
	const rows = await call("frappe.client.get_list", {
		doctype: "Company",
		fields: ["name", "default_currency", "default_receivable_account", "default_cash_account"],
		limit_page_length: 1,
	});
	companyCache = (Array.isArray(rows) ? rows[0] : null) || {};
	return companyCache;
}

function filterList(rows, args, searchFields) {
	let output = [...rows];

	if (args.search) {
		const needle = args.search.toLowerCase();
		output = output.filter((row) =>
			searchFields.some((field) => String(row[field] || "").toLowerCase().includes(needle)),
		);
	}

	if (args.status) {
		const status = args.status === "Quotations" ? "Quote" : args.status;
		output = output.filter((row) => row.status === status);
	}

	if (args.category) {
		output = output.filter((row) => row.category === args.category);
	}

	return output;
}
